(ns ycweb.app.admin.storeplans.storeplans
  (:require
   [rum.core :as rum]
   [clojure.set :as set]
   [util.string :as str]
   [ycweb.app.admin.storeplans.merge-summary]
   [ycweb.app.admin.storeplans.summary]
   [ycweb.app.admin.storeplans.overview]
   [ycweb.app.dashboard.presence :as presence]
   [ycweb.components.common :as cmn]
   [ycweb.components.feedback :as feedback]
   [ycweb.components.datatables.datatables :as datatables]
   [ycweb.components.flow :as flow]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.components.transitions :as t]
   [ycweb.components.toaster :as toaster]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as yuc]
   [ycweb.util.urlstate :as urlstate]))

(def table-name :admin.store-plans/datatable)
;;=====================================================================================================================
;; MERGE STORE PLAN LOGIC - Transitions to a summary page before merging
;;=====================================================================================================================
(defmethod grid/action :admin.store-plans/merge-check [app-atom _ _]
  (let [process-atom (cmn/process-cursor app-atom)
        table-atom (datatables/table-cursor app-atom :admin.store-plans/datatable)
        selected-row-ids (cmn/curact table-atom :select)
        selected-rows (vals (datatables/rows app-atom :admin.store-plans/datatable selected-row-ids))
        active-stores (filter :store/active selected-rows)
        active-store-plans (filter :store-plan/active selected-rows)]
    (cond
      (= (count active-store-plans) (count selected-rows))
      (grid/message-modal app-atom
        {:title "Error"
         :text "Inactive store plans selected, only able to merge active store plans"
         :type :error
         :confirm-text "OK"})

      (> (count active-stores) 1)
      (grid/message-modal app-atom
        {:title "Error"
         :text "Multiple active stores selected. You are only able to merge store plans from multiple inactive stores into a store plan linked to an active store"
         :type :error
         :confirm-text "OK"})

      :else
      (grid/open-modal app-atom :grid/confirm
        {:title "Merge Store Plans"
         :text
         (feedback/feedback :warning
           [:b "Merge Summary Screen"]
           [:div
            [:p "Please check the correct plans have been selected"]
            [:p "Confirming will take you to a summary page before merging, allowing you to confirm the changes."]
            (forms/input "Days" "" (rum/cursor-in process-atom [:merged-store-plan :days])
              {:hint "Used to calculate store sales data, among other things"
               :default 90})])
         :right-text "View Merge Summary"
         :confirm-key :admin.store-plans/validate-merge
         :confirm-data {:selected-row-ids selected-row-ids}}))))

(defmethod grid/action :admin.store-plans/validate-merge [app-atom {:keys [selected-row-ids]} _]
  (toaster/open-overlay app-atom :admin.store-plans/loading {})
  (comms/send :admin.store-plans/validate-merge.store-plans
    {:store-plan/id selected-row-ids}))

(rum/defc display-variation < rum/reactive [item]
  [:li (str (:variation/name item) " : " (:product/name item))])

(rum/defc modal-list-item < rum/reactive [item]
  [:div
   (feedback/feedback :error
     [:b (:msg item)]
     (when (yuc/presence (:duplicate-variations item))
       [:div
        {:style
         {:max-height "150px"
          :overflow-y :scroll}}
        (into [:ul]
          (map
            display-variation
            (:duplicate-variations item)))]))])

(defmethod comms/receive :admin.store-plans/validate-merge.store-plans [app-atom e]
  (toaster/close-overlay app-atom :admin.store-plans/loading)
  (let [process-atom (cmn/process-cursor app-atom)
        days (cmn/curact process-atom [:merged-store-plan :days])]
    (cond
      (:errors e)
      (grid/list-modal app-atom
        {:title "Merge Conflicts"
         :list
         (into
           [:div]
           (map modal-list-item (:errors e)))})

      :else
      (let [stores-idx (yuc/index-by :id (:stores e))

            store-plans-idx
            (yuc/index-by :store-plan/id
              (conj (:inactive-store-plans e) (:active-store-plan e)))

            active-items-idx
            (yuc/index-by :variation_id (:active-store-plan-items e))

            store-plan-items
            (->>
              (:inactive-store-plan-items e)
              (reduce
                (fn [acc inactive-store-plan-item]
                  (let [item-vid (:variation_id inactive-store-plan-item)
                        item-match (get acc item-vid)]
                    (cond
                      item-match
                      (update acc item-vid
                        assoc :merge/match item-match)

                      :else
                      (conj acc
                        {item-vid (assoc inactive-store-plan-item :merge/new? true)}))))
                active-items-idx)
              (map
                (fn [[_ {:keys [id storeplan_id variation_id minqtyfordisplay qty instock_qty] :as item}]]
                  (let [{:store-plan/keys [store_id]} (get store-plans-idx storeplan_id)
                        store (get stores-idx store_id)]
                    (yuc/maybe assoc
                      {:variation/id variation_id
                       :store-plan/mdq (or minqtyfordisplay 0)
                       :store-plan/total-qty (or qty 0)
                       :store-plan/in-store-stock-qty (or instock_qty 0)
                       :store/id store_id
                       :store-plan/id storeplan_id
                       :store-plan-item/id id
                       :store/name (:name store)}
                      :merge/new? (:merge/new? item)
                      :merge/match (:merge/match item)))))
              (yuc/index-by :variation/id))

            store-plan
            (assoc (:active-store-plan e) :store-plan/items store-plan-items)]

        (swap! process-atom assoc :merged-store-plan
          {:store-plan store-plan
           :store-plans (:inactive-store-plans e)})
        (urlstate/redirect app-atom "storeplans" "merge-summary" {:days days})))))

;;=====================================================================================================================

(defn boolean-fn [app-atom row col v]
  [:span (if v "Yes" "No")])

(defn store-plan-view [app-atom row col v]
  [[:a
    (urlstate/href-props app-atom "storeplans" "summary"
      {:id (:store-plan/id row)}) "View"]
   " | "
   [:a
    (urlstate/href-props app-atom "storeplans" "new-summary"
      {:id (:store-plan/id row)}) "View new Summary"]])

(defn store-plan-table-fns [app-atom]
  {:column-fns
   {:boolean-fn boolean-fn
    :view-fn store-plan-view}})

(def store-plan-column-defs
  [{:key :store-plan/id
    :type :selector}
   {:key :store/name
    :title "Store"}
   {:key :store-plan/name
    :title "Plan Name"}
   {:key :store-plan/active
    :title "Active"
    :align-class :u-text-center
    :render
    {:render-k :boolean-fn}}
   {:key :store-plan/id
    :title "View"
    :align-class :u-text-center
    :render
    {:render-k :view-fn}}])

(defn filter-active-store-plans-fn [data-map]
  (into {}
    (filter (fn [[_ v]] (:store-plan/active v))
      data-map)))

(defn store-plan-grid-columns [app-atom]
  {:table-name table-name
   :row-id :store-plan/id
   :data-source {:cursor [:admin.storeplans :store-plans]}
   :options
   {:order :asc
    :sort-title "Store"}
   :buttons
   [{:text "Store Plans Overview"
     :action :admin.store-plans/transition-to-overview
     :extends :edit
     :multi-select true}
    (when (cmn/has-permission? app-atom "SiteAdmin:Teams:Development")
      {:text "Merge"
       :action :admin.store-plans/merge-check
       :extends :edit})
    {:text "Refresh"
     :action :admin.store-plans/refresh-store-plans}
    {:extends :toggle-select-all
     :raw-data-override-fn filter-active-store-plans-fn}]
   :select? {:style :os}
   :column-defs store-plan-column-defs})

(rum/defc store-plans < rum/reactive [app-atom]
  [:div
   (headings/heading "Store Plans" {:type :h1 :size :large})
   (datatables/table app-atom
     :admin.store-plans/datatable
     (store-plan-table-fns app-atom))
   (toaster/loading-overlay app-atom :admin.store-plans/loading)])

(defmethod flow/render-screen :admin.storeplans [app-atom _]
  (store-plans app-atom))

(defmethod t/transition-open :admin.storeplans [app-atom _ _]
  (let [process-atom (cmn/process-cursor app-atom)
        store-plans-data (cmn/curact process-atom :store-plans)]
    (when (empty? store-plans-data)
      (comms/send :admin.store-plans/list {}))))

(defmethod t/transition-after-open :admin.storeplans [app-atom _ _]
  (presence/open app-atom {:context/ident :admin.store-plans})
  (datatables/initiate app-atom (store-plan-grid-columns app-atom) {}))

(defmethod grid/action :admin.store-plans/refresh-store-plans [app-atom _ _]
  (toaster/open-overlay app-atom :admin.store-plans/datatable)
  (comms/send :admin.store-plans/list {}))

(defmethod comms/receive :admin.store-plans/list [app-atom e]
  (toaster/close-overlay app-atom :admin.store-plans/datatable)
  (let [process-atom (cmn/process-cursor app-atom)]
    (swap! process-atom assoc :store-plans (yuc/index-by :store-plan/id (:rows e)))))

(defmethod grid/action :admin.store-plans/transition-to-overview [app-atom _ _]
  (let [table-atom (datatables/table-cursor app-atom :admin.store-plans/datatable)
        selected-row-ids (cmn/curact table-atom :select)
        selected-ids (str/join "," selected-row-ids)
        selected-rows (vals (datatables/rows app-atom :admin.store-plans/datatable selected-row-ids))
        active-store-plans (filter :store-plan/active selected-rows)]
    (cond
      (= (count active-store-plans) (count selected-rows))
      (urlstate/redirect app-atom "storeplans" "overview" {:ids selected-ids})

      :else
      (grid/message-modal app-atom
        {:title "Error"
         :text "Inactive store plans selected"
         :type :error
         :confirm-text "OK"}))))

(defmethod grid/action :admin.store-plans/transition-to-new-summary [app-atom _ _]
  (let [table-atom (datatables/table-cursor app-atom :admin.store-plans/datatable)
        selected-row-ids (cmn/curact table-atom :select)]
    (cond
      (= (count selected-row-ids) 1)
      (urlstate/redirect app-atom "storeplans" "new-summary" {:id (first selected-row-ids)})

      :else
      (grid/message-modal app-atom
        {:title "Error"
         :text "Multiple plans selected"
         :type :error
         :confirm-text "OK"}))))